import { validateConfig, z } from '@repo/config';

// Local Supabase defaults — only used when VITE_* vars are not set.
// These are the standard keys for every local Supabase instance and are safe to hard-code.
const LOCAL_SUPABASE_URL = 'http://127.0.0.1:54321';
const LOCAL_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

const WebConfigSchema = z.object({
  supabaseUrl: z.string().url().default(LOCAL_SUPABASE_URL),
  supabaseAnonKey: z.string().min(1).default(LOCAL_SUPABASE_ANON_KEY),
  appEnv: z.enum(['local', 'staging', 'production']).default('local'),
  posthogToken: z.string().min(1).optional(),
  posthogHost: z.string().url().optional(),
  posthogAppHost: z.string().url().optional(),
  posthogProjectId: z.string().min(1).optional(),
  enablePostHogReplay: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
  enableAnalytics: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  // Gates the SMS daily-digest experience: the onboarding opt-in step AND the
  // Settings SMS UI. Twilio is configured and the digest ships, so this now
  // defaults to ON — an absent env var means enabled. It is retained purely as
  // a kill-switch: set VITE_ENABLE_SMS=false to pull the step and the Settings
  // section without a code change. Build-time flag.
  enableSms: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
});

export type WebConfig = z.infer<typeof WebConfigSchema>;

// Validated at module load time. Throws ConfigValidationError on startup
// if a required variable is missing or malformed — never silently in business logic.
export const config = validateConfig(
  WebConfigSchema,
  {
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    appEnv: import.meta.env.VITE_APP_ENV,
    posthogToken: import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN,
    posthogHost: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    posthogAppHost: import.meta.env.VITE_PUBLIC_POSTHOG_APP_HOST,
    posthogProjectId: import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_ID,
    enablePostHogReplay: import.meta.env.VITE_ENABLE_POSTHOG_REPLAY,
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS,
    enableSms: import.meta.env.VITE_ENABLE_SMS,
  },
  'web',
);
